import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { Table, TableHead, TableBody, TableRow, TableCell, TableFooter } from "@mui/material";

import Dialog, { CloseDialog } from "components/Dialog";
import Compass from "components/Compass";
import Button from "components/Button";
import Input from "components/Input";

import { getLabel } from "utils/dcfExport/details";

import { selectCommunityModelDir, selectModelsInRun } from "store/community/selectors";
import { updateModelDuplicates } from "store/community/thunk";

import ModelIcon from "assets/images/icons/model.svg";
import classes from "./styles.module.scss";

const DirectionDialog = ({
    isDialogOpen,
    closeDialog,
    name,
    modelId,
    updateModelDuplicates,
    modelDir: { models: commModelData },
    modelsInRun = {},
}) => {
    const { baseDirection, copyDirections: initDirections } = commModelData[modelId]?.countDirection || {};
    const [copyDirections, setCopyDirections] = useState(initDirections || {});
    const [isInvalid, setIsInvalid] = useState(false);
    const [notInRun, setNotInRun] = useState([]);

    const getNumDuplicates = (copyDirections) => {
        return Object.values(copyDirections)
            .map((val) => (isNaN(val) ? 0 : val))
            .reduce((a, b) => a + b, 0);
    };

    // Temporarily prevent adding new directions to an already run model
    useEffect(() => {
        setNotInRun([]);
        if (isDialogOpen && !!modelsInRun[modelId]) {
            const runDirections = Object.entries(copyDirections).filter(([direction, value]) => value === 0).map(([direction, value]) => direction);
            setNotInRun(runDirections);
        }
    }, [isDialogOpen]);

    useEffect(() => {
        const numDuplicates = getNumDuplicates(copyDirections);

        if (numDuplicates <= 0 && !isInvalid) {
            setIsInvalid(true);
        } else if (numDuplicates > 0 && isInvalid) {
            setIsInvalid(false);
        }

        // Temporarily prevent adding new directions to an already run model
        if (notInRun.length > 0) {
            setIsInvalid(Object.entries(copyDirections).reduce((acc, [direction, value]) => {
                if (!notInRun.includes(direction) && !value) {
                    acc = true;
                }
                return acc;
            }, false));
        }

    }, [copyDirections]);

    if (!copyDirections) {
        return <></>;
    }

    return (
        <Dialog
            open={isDialogOpen}
            style={{ padding: "0" }}
            classes={{ paperClass: classes.dialogContainer }}
            onClose={closeDialog}
        >
            <CloseDialog closeActions={() => closeDialog()} />
            <div className={classes.directionDialogContainer}>
                <h2 className={classes.rotateHouseTitle}>Count & Direction</h2>
                <div className={classes.orientationWrapper}>
                    <div className={classes.orientation}>
                        <img src={ModelIcon} />
                        <Input
                            className={classes.orientationSelect}
                            label="Model Name"
                            disabled={true}
                            input={{
                                value: name,
                            }}
                        />
                    </div>
                    <div className={classes.orientation}>
                        <Compass
                            facingId={baseDirection}
                            rotationOptions={{
                                0: 180,
                                1: 135,
                                2: 90,
                                3: 45,
                                4: 0,
                                5: -45,
                                6: -90,
                                7: -135,
                            }}
                        />
                        <Input
                            className={classes.orientationSelect}
                            label="Modelled Direction"
                            disabled={true}
                            input={{
                                value: getLabel("facingDirection", baseDirection) || "",
                            }}
                        />
                    </div>
                </div>
                <div className={classes.divider}></div>

                <Table className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Direction</TableCell>
                            <TableCell align="right" className={classes.columnHeader}>
                                No. of Duplicates
                            </TableCell>

                            <TableCell>Direction</TableCell>
                            <TableCell align="right" className={classes.columnHeader}>
                                No. of Duplicates
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>South</TableCell>
                            <TableCell align="right">
                                <Input
                                    type={"number"}
                                    input={{
                                        value: copyDirections.S,
                                        min: 0,
                                        onChange: (value) => {
                                            setCopyDirections((prev) => ({ ...prev, S: value }));
                                        },
                                    }}
                                    disabled={notInRun.includes("S")}
                                />
                            </TableCell>

                            <TableCell>South-East</TableCell>
                            <TableCell align="right">
                                <Input
                                    type={"number"}
                                    input={{
                                        value: copyDirections.SE,
                                        min: 0,
                                        onChange: (value) => {
                                            setCopyDirections((prev) => ({ ...prev, SE: parseInt(value) }));
                                        },
                                    }}
                                    disabled={notInRun.includes("SE")}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>East</TableCell>
                            <TableCell align="right">
                                <Input
                                    type={"number"}
                                    input={{
                                        value: copyDirections.E,
                                        min: 0,
                                        onChange: (value) => {
                                            setCopyDirections((prev) => ({ ...prev, E: parseInt(value) }));
                                        },
                                    }}
                                    disabled={notInRun.includes("E")}
                                />
                            </TableCell>

                            <TableCell>North-East</TableCell>
                            <TableCell align="right">
                                <Input
                                    type={"number"}
                                    input={{
                                        value: copyDirections.NE,
                                        min: 0,
                                        onChange: (value) => {
                                            setCopyDirections((prev) => ({ ...prev, NE: parseInt(value) }));
                                        },
                                    }}
                                    disabled={notInRun.includes("NE")}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>North</TableCell>
                            <TableCell align="right">
                                <Input
                                    type={"number"}
                                    input={{
                                        value: copyDirections.N,
                                        min: 0,
                                        onChange: (value) => {
                                            setCopyDirections((prev) => ({ ...prev, N: parseInt(value) }));
                                        },
                                    }}
                                    disabled={notInRun.includes("N")}
                                />
                            </TableCell>

                            <TableCell>North-West</TableCell>
                            <TableCell align="right">
                                <Input
                                    type={"number"}
                                    input={{
                                        value: copyDirections.NW,
                                        min: 0,
                                        onChange: (value) => {
                                            setCopyDirections((prev) => ({ ...prev, NW: parseInt(value) }));
                                        },
                                    }}
                                    disabled={notInRun.includes("NW")}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>West</TableCell>
                            <TableCell align="right">
                                <Input
                                    type={"number"}
                                    input={{
                                        value: copyDirections.W,
                                        min: 0,
                                        onChange: (value) => {
                                            setCopyDirections((prev) => ({ ...prev, W: parseInt(value) }));
                                        },
                                    }}
                                    disabled={notInRun.includes("W")}
                                />
                            </TableCell>

                            <TableCell>South-West</TableCell>
                            <TableCell align="right">
                                <Input
                                    type={"number"}
                                    input={{
                                        value: copyDirections.SW,
                                        min: 0,
                                        onChange: (value) => {
                                            setCopyDirections((prev) => ({ ...prev, SW: parseInt(value) }));
                                        },
                                    }}
                                    disabled={notInRun.includes("SW")}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell>Total</TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell align="right">{getNumDuplicates(copyDirections)}</TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
                {isInvalid && <div className={classes.error}>Total should be at least 1</div>}
                <div className={classes.buttonsRow}>
                    <Button
                        type="hollow"
                        style={{ width: "144px" }}
                        onClick={() => {
                            setCopyDirections(initDirections);
                            closeDialog();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type={"primary"}
                        style={{ width: "144px" }}
                        onClick={async () => {
                            await updateModelDuplicates(modelId, copyDirections);
                            closeDialog();
                        }}
                        disabled={isInvalid}
                    >
                        Confirm
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

const mapStateToProps = createStructuredSelector({
    modelDir: selectCommunityModelDir,
    modelsInRun: selectModelsInRun,
});

const mapDispatchToProps = (dispatch) => ({
    updateModelDuplicates: async (modelId, countDirection) => {
        dispatch(updateModelDuplicates(modelId, countDirection));
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(DirectionDialog);
